import { useEffect, useMemo } from "react";
import { View, Text, Pressable, ActivityIndicator } from "react-native";
import { Snowflake, MapPin, AlertCircle } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { useCycles } from "@src/packages/cycles/hooks/useCycles";
import { useFrozenSpecimens, type Specimen } from "./useSpecimens";
import {
  getOpenConsumingCycleId,
  getSelectionSourceCycleId,
} from "./frozenSpecimenAvailability";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const EMBER = {
  colors: {
    warm: "#8B3A1A",
    warmDark: "#1A0A02",
    warmLight: "#FDDAB0",
    coral: "#A8563A",
    teal: "#5E6E3E",
    blue: "#6B5A4E",
    green: "#4E7342",
    gold: "#B08030",
    cryo: "#7A8B8E",
    embryo: "#C97B5E",
    text: "#2A2522",
    textMid: "#5A4D45",
    textLight: "#8A7D74",
    textFaint: "#B0A49A",
    bg: "#FAF6F2",
    bgEnd: "#F5F0EB",
    border: "#E8E0D8",
    surface: "#FFFFFF",
  },
} as const;

export type FrozenSpecimenSelection = {
  specimenIds: string[];
  sourceCycleId: string | null;
};

type Props = {
  type: "oocyte" | "embryo";
  selectedIds: string[];
  onChange: (selection: FrozenSpecimenSelection) => void;
  allowedCycleId?: string | null;
};

const TYPE_LABEL: Record<Props["type"], { singular: string; plural: string }> =
  {
    oocyte: { singular: "egg", plural: "eggs" },
    embryo: { singular: "embryo", plural: "embryos" },
  };

function activeFacility(specimen: Specimen): string {
  return (
    specimen.storageLocations.find(
      (l) => l.isActive && l.storageType === "frozen",
    )?.facility ?? "Unknown facility"
  );
}

function formatCollectedDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function FrozenSpecimenSelector({
  type,
  selectedIds,
  onChange,
  allowedCycleId = null,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("FrozenSpecimenSelector");
  const { specimens, isLoading } = useFrozenSpecimens();
  const { data: cycles } = useCycles();
  const labels = TYPE_LABEL[type];

  const filtered = useMemo(
    () => specimens.filter((s) => s.type === type),
    [specimens, type],
  );

  const byFacility = useMemo(() => {
    const groups = new Map<string, Specimen[]>();
    for (const s of filtered) {
      const facility = activeFacility(s);
      const list = groups.get(facility) ?? [];
      list.push(s);
      groups.set(facility, list);
    }
    return Array.from(groups.entries());
  }, [filtered]);

  const selectedSourceCycle = useMemo(() => {
    return getSelectionSourceCycleId(filtered, selectedIds);
  }, [selectedIds, filtered]);

  const openCycleIds = useMemo(
    () =>
      new Set(
        (cycles ?? [])
          .filter((cycle) => cycle.status === "open")
          .map((cycle) => cycle.id),
      ),
    [cycles],
  );

  const reservedOpenCycleBySpecimen = useMemo(() => {
    const reservations = new Map<string, string>();
    for (const specimen of filtered) {
      const cycleId = getOpenConsumingCycleId(
        specimen,
        openCycleIds,
        allowedCycleId,
      );
      if (cycleId) reservations.set(specimen.id, cycleId);
    }
    return reservations;
  }, [allowedCycleId, filtered, openCycleIds]);

  // A stale prefill can contain a specimen that another open cycle has
  // reserved since the CryoVault action was opened. Remove it from the
  // pending selection once the cycle list has resolved so the confirmation
  // request can never carry a known-conflicting ID.
  useEffect(() => {
    if (cycles === undefined || reservedOpenCycleBySpecimen.size === 0) return;
    const availableIds = selectedIds.filter(
      (id) => !reservedOpenCycleBySpecimen.has(id),
    );
    if (availableIds.length === selectedIds.length) return;
    onChange({
      specimenIds: availableIds,
      sourceCycleId: getSelectionSourceCycleId(filtered, availableIds),
    });
  }, [cycles, filtered, onChange, reservedOpenCycleBySpecimen, selectedIds]);

  const handleToggle = (specimen: Specimen) => {
    const isSelected = selectedIds.includes(specimen.id);
    let newIds: string[];
    if (isSelected) {
      newIds = selectedIds.filter((id) => id !== specimen.id);
    } else {
      newIds = [...selectedIds, specimen.id];
    }
    const sourceCycleId =
      newIds.length > 0
        ? (filtered.find((s) => s.id === newIds[0])?.cycleId ?? null)
        : null;
    onChange({ specimenIds: newIds, sourceCycleId });
  };

  if (isLoading) {
    return (
      <View style={{ paddingVertical: 16, alignItems: "center" }}>
        <ActivityIndicator color={EMBER.colors.warm} />
      </View>
    );
  }

  if (filtered.length === 0) {
    return (
      <View
        style={{
          borderWidth: 1,
          borderColor: EMBER.colors.border,
          borderRadius: 12,
          padding: 14,
          backgroundColor: "rgba(0,0,0,0.02)",
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        <Snowflake size={16} color={EMBER.colors.textFaint} strokeWidth={1.5} />
        <Text
          className="font-dm-sans"
          style={{
            flex: 1,
            fontSize: 13,
            color: EMBER.colors.textMid,
            lineHeight: 18,
          }}
        >
          {`No frozen ${labels.plural} in your CryoVault yet. Once your clinic logs them, they'll appear here.`}
        </Text>
      </View>
    );
  }

  const showFewEggsWarning =
    type === "oocyte" && selectedIds.length > 0 && selectedIds.length < 4;

  return (
    <View style={{ gap: 14 }}>
      {byFacility.map(([facility, items]) => (
        <View key={facility} style={{ gap: 6 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              marginBottom: 2,
            }}
          >
            <MapPin
              size={12}
              color={EMBER.colors.textFaint}
              strokeWidth={1.5}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "500",
                color: EMBER.colors.textMid,
              }}
            >
              {facility}
            </Text>
          </View>
          {items.map((specimen, idx) => {
            const selected = selectedIds.includes(specimen.id);
            const blockedByCycle =
              !selected &&
              selectedSourceCycle !== null &&
              specimen.cycleId !== selectedSourceCycle;
            const reservedByOpenCycle =
              reservedOpenCycleBySpecimen.get(specimen.id) ?? null;
            const blockedByReservation =
              !selected && reservedByOpenCycle !== null;
            const blocked = blockedByCycle || blockedByReservation;
            return (
              <Pressable
                key={specimen.id}
                onPress={() => {
                  if (blocked) return;
                  capturePress(
                    pressLabel(
                      `${labels.singular.charAt(0).toUpperCase()}${labels.singular.slice(1)} #${idx + 1}`,
                    ),
                    "specimen_row",
                  );
                  handleToggle(specimen);
                }}
                disabled={blocked}
                style={({ pressed }) => ({
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  paddingHorizontal: 14,
                  paddingVertical: 11,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: selected
                    ? EMBER.colors.warm
                    : EMBER.colors.border,
                  backgroundColor: selected
                    ? `${EMBER.colors.warm}08`
                    : pressed
                      ? "rgba(0,0,0,0.02)"
                      : "rgba(0,0,0,0.01)",
                  opacity: blocked ? 0.45 : 1,
                })}
              >
                <View
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    borderWidth: 1.5,
                    borderColor: selected
                      ? EMBER.colors.warm
                      : EMBER.colors.textFaint,
                    backgroundColor: selected
                      ? EMBER.colors.warm
                      : "transparent",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {selected && (
                    <View
                      style={{
                        width: 8,
                        height: 2,
                        backgroundColor: "#fff",
                        borderRadius: 1,
                      }}
                    />
                  )}
                </View>
                <View style={{ flex: 1 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: selected ? "600" : "400",
                        color: EMBER.colors.text,
                      }}
                    >
                      {`${labels.singular.charAt(0).toUpperCase()}${labels.singular.slice(1)} #${idx + 1}`}
                    </Text>
                    {specimen.isDonor === true && (
                      <View
                        style={{
                          backgroundColor: `${EMBER.colors.teal}20`,
                          paddingHorizontal: 6,
                          paddingVertical: 1,
                          borderRadius: 4,
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 9,
                            fontWeight: "600",
                            color: EMBER.colors.teal,
                            letterSpacing: 0.4,
                          }}
                        >
                          DONOR
                        </Text>
                      </View>
                    )}
                  </View>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      color: EMBER.colors.textLight,
                      marginTop: 1,
                    }}
                  >
                    {`Collected ${formatCollectedDate(specimen.collectedAt)}`}
                    {blockedByCycle
                      ? ` · ${t("care.select_specimens.different_retrieval")}`
                      : blockedByReservation
                        ? ` · ${t("care.select_specimens.reserved_by_open_cycle")}`
                        : ""}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      ))}
      {showFewEggsWarning && (
        <View
          style={{
            flexDirection: "row",
            gap: 8,
            padding: 12,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: `${EMBER.colors.gold}40`,
            backgroundColor: `${EMBER.colors.gold}10`,
          }}
        >
          <AlertCircle
            size={14}
            color={EMBER.colors.gold}
            strokeWidth={1.5}
            style={{ marginTop: 1 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 12,
              color: EMBER.colors.textMid,
              lineHeight: 17,
            }}
          >
            Most clinics recommend more eggs for one viable cycle — talk to your
            team about your options.
          </Text>
        </View>
      )}
      {reservedOpenCycleBySpecimen.size > 0 && (
        <View
          style={{
            flexDirection: "row",
            gap: 8,
            padding: 12,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: `${EMBER.colors.blue}40`,
            backgroundColor: `${EMBER.colors.blue}10`,
          }}
        >
          <Snowflake
            size={14}
            color={EMBER.colors.blue}
            strokeWidth={1.5}
            style={{ marginTop: 1 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 12,
              color: EMBER.colors.textMid,
              lineHeight: 17,
            }}
          >
            {t("care.select_specimens.reserved_notice")}
          </Text>
        </View>
      )}
    </View>
  );
}
